"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CollectionModel = void 0;
const tslib_1 = require("tslib");
const form_1 = require("../form");
const brightnessCorrection_state_1 = require("./brightnessCorrection/brightnessCorrection.state");
const collection_base_1 = require("./collection.base");
const collection_loader_1 = require("./collection.loader");
const collection_state_1 = require("./collection.state");
const cropParameter_state_1 = require("./cropParameter/cropParameter.state");
const drawable_state_1 = require("./drawable/drawable.state");
const capture_provider_1 = require("./fields/provider/capture.provider");
const collection_provider_1 = require("./fields/provider/collection.provider");
const imageVisibility_state_1 = require("./imageVisibility/imageVisibility.state");
/**
 * This class is a proxy to store all its data inside of the state.
 *
 * This is done to allow a easy place to record all changes to the Collection,
 * which can be used for editing, undo/redo stacks.
 *
 * **Do not** add any properties directly to this class
 */
class CollectionModel extends collection_base_1.CollectionModelBase {
    constructor(form, loader) {
        super(null, null, form);
        this.brightnessCorrectionState = {};
        this.cropParameterState = {};
        this.imageVisibilityState = {};
        /** State proxy which can be used to track edits */
        this.proxyState = null;
        this._fieldCache = {};
        this._fieldCacheCount = 0;
        this.loader = loader;
        this._state = new collection_state_1.CollectionModelState(this.loader);
    }
    static createLoader() {
        return new collection_loader_1.IkeCollectionLoaderFromState();
    }
    get state() {
        if (this.isEditing && this.proxyState) {
            return this.proxyState;
        }
        return this._state;
    }
    get id() {
        return this.state.id;
    }
    get instanceId() {
        return this.state.instanceId;
    }
    get uuid() {
        return this.state.uuid;
    }
    get revision() {
        return this.state.revision;
    }
    get jobId() {
        return this.state.jobId;
    }
    get updatedAt() {
        return this.state.updatedAt;
    }
    get updatedBy() {
        return this.state.updatedBy;
    }
    get createdAt() {
        return this.state.createdAt;
    }
    get collectedBy() {
        return this.state.collectedBy;
    }
    get collectedWith() {
        return this.state.collectedWith;
    }
    get assignedTo() {
        return this.state.assignedTo;
    }
    /**
     * @deprecated Use {@link getCollectedAt} instead.
     */
    get collectedAt() {
        if (this.state.collectedAt != null) {
            return this.state.collectedAt;
        }
        const captureIds = capture_provider_1.CaptureProvider.getCaptureIds(this);
        let earliestDate = null;
        for (const captureId of captureIds) {
            const fetchable = this.loader.Capture.getById(captureId);
            // Only calculate when all captures are loaded
            if (!fetchable.hasFetched) {
                return null;
            }
            const capture = fetchable.value;
            if (capture.collectedAt == null) {
                continue;
            }
            if (earliestDate == null || capture.collectedAt.iso < earliestDate) {
                earliestDate = capture.collectedAt.iso;
            }
        }
        if (earliestDate != null) {
            this.state.collectedAt = new Date(earliestDate);
        }
        return this.state.collectedAt;
    }
    getCollectedAt() {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            if (this.state.collectedAt != null) {
                return this.state.collectedAt;
            }
            const captureIds = capture_provider_1.CaptureProvider.getCaptureIds(this);
            let earliestDate = null;
            for (const captureId of captureIds) {
                const fetchable = this.loader.Capture.getById(captureId);
                const capture = yield fetchable.fetch();
                if (capture.collectedAt == null) {
                    continue;
                }
                if (earliestDate == null || capture.collectedAt.iso < earliestDate) {
                    earliestDate = capture.collectedAt.iso;
                }
            }
            if (earliestDate != null) {
                this.state.collectedAt = new Date(earliestDate);
            }
            return this.state.collectedAt;
        });
    }
    /**
     * Look up the collection status using the status field from the metadata.
     *
     * If the collection does not have a status field, no field will be returned.
     */
    get status() {
        const statusField = this.schema.metadata.getStatusField();
        if (statusField == null) {
            return null;
        }
        return this.getField(statusField.id);
    }
    /**
     * Look up the collection map color using the map color field from the metadata.
     * If the collection does not have a map color field, no field will be returned.
     */
    get mapMarkerColor() {
        const mapColorField = this.schema.metadata.getMapColorField();
        if (mapColorField == null) {
            return null;
        }
        let color = null;
        if (mapColorField instanceof form_1.FormModelFieldSelectList) {
            color = mapColorField.options[0].color;
            const field = this.getField(mapColorField.id);
            if (field) {
                mapColorField.options.forEach((option) => {
                    if (option.value === field.value) {
                        color = option.color;
                    }
                });
            }
        }
        else if (mapColorField instanceof form_1.FormModelFieldNestedList) {
            const field = this.getField(mapColorField.id);
            color = '#a0aec0';
            if (field) {
                if (field.hasValue()) {
                    for (let i = 0; i < mapColorField.options.values.length; i++) {
                        if (mapColorField.options.values[i].value === field.value[0]) {
                            color = mapColorField.options.values[i].color;
                        }
                    }
                }
            }
        }
        return color;
    }
    /**
     * Create a new proxy state layer, this can be used to track changes to a collection.
     * @returns Created proxy state
     */
    beginEdit(editState) {
        const newState = editState(this._state);
        this.proxyState = newState;
        return newState;
    }
    /**
     * Remove the proxy state layer, this will undo any changes made to a collection
     */
    finishEdit() {
        if (this.proxyState == null) {
            throw new Error('Unable to finish editing, no edit in place');
        }
        this.proxyState = null;
    }
    get isEditing() {
        return this.proxyState != null;
    }
    get collection() {
        return this;
    }
    /**
     * Get the time the collection was created, using the collectedAt if it exists otherwise the time it was uploaded
     *
     * @deprecated Use {@link getRecordedAt} instead.
     */
    get recordedAt() {
        return this.collectedAt == null ? this.createdAt : this.collectedAt;
    }
    getRecordedAt() {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const collectedAt = yield this.getCollectedAt();
            return collectedAt !== null && collectedAt !== void 0 ? collectedAt : this.createdAt;
        });
    }
    getDrawableState(captureId) {
        if (!this.state.drawableStates.has(captureId)) {
            this.state.drawableStates.set(captureId, new drawable_state_1.CollectionModelDrawableState(this, captureId));
        }
        const drawableState = this.state.drawableStates.get(captureId);
        if (!drawableState) {
            throw new Error(`Unable to get drawable state for capture ${captureId}`);
        }
        return drawableState;
    }
    getImageVisibilityState(captureId) {
        let existing = this.imageVisibilityState[captureId];
        if (existing == null) {
            existing = this.imageVisibilityState[captureId] = new imageVisibility_state_1.CollectionModelImageVisibilityState(this, captureId);
        }
        return existing;
    }
    getBrightnessCorrectionState(captureId) {
        let existing = this.brightnessCorrectionState[captureId];
        if (existing == null) {
            existing = this.brightnessCorrectionState[captureId] = new brightnessCorrection_state_1.CollectionModelBrightnessCorrectionState(this, captureId);
        }
        return existing;
    }
    getCropParameterState(captureId) {
        let existing = this.cropParameterState[captureId];
        if (existing == null) {
            existing = this.cropParameterState[captureId] = new cropParameter_state_1.CollectionModelCropParameterState(this, captureId);
        }
        return existing;
    }
    /**
     * Find a field using its unique field Id
     * Used for quickly looking up field links.
     *
     * @param fieldId Unique field Id including subformId
     * @returns collection field if it exists
     */
    findField(fieldId) {
        let existing = this._fieldCache[fieldId];
        if (existing !== undefined) {
            return existing;
        }
        let output = null;
        this.forEachField((field) => {
            if (field.id === fieldId) {
                output = field;
                return false; // abort the search
            }
            return true;
        });
        this._fieldCache[fieldId] = output;
        this._fieldCacheCount++;
        // Paranoia incase the cache gets too large
        if (this._fieldCacheCount > 1000) {
            this._fieldCacheCount = 0;
            this._fieldCache = {};
        }
        return output;
    }
    /**
     * Collections are not loaded with all their information.
     *
     * Wait for all the linked data to load in
     *
     * If any object in the returned list is null, it means the collection failed to load something.
     *
     * @return List of objects that were loaded
     */
    ready() {
        return tslib_1.__awaiter(this, void 0, void 0, function* () {
            const captureIds = capture_provider_1.CaptureProvider.getCaptureIds(this);
            const collectionIds = collection_provider_1.CollectionProvider.getCollectionIds(this);
            const fetchables = [];
            const captures = yield Promise.all(captureIds.map((id) => this.loader.Capture.getById(id).fetch()));
            for (let i = 0; i < captures.length; i++) {
                const capture = captures[i];
                if (capture == null) {
                    throw new Error('Unable to load capture: ' + captureIds[i]);
                }
                if (capture.calibrationid == null) {
                    continue;
                }
                fetchables.push(this.loader.Calibration.getById(capture.calibrationid).fetch());
            }
            for (const collectionId of collectionIds) {
                fetchables.push(this.loader.CollectionSummary.getById(collectionId).fetch());
            }
            // Note: We could fetch all linked collection models here, but if we did, it would
            // recursively load all of the linked collections, which may trigger an infinite loop or
            // an unnecessarily large number of fetches.
            // So if you need to load linked collection models, use `CollectionModelState.getCollectionModelAsync()`
            // or `CollectionModelFieldVector.getLinkedCollection()`.
            return yield Promise.all(fetchables);
        });
    }
}
exports.CollectionModel = CollectionModel;
//# sourceMappingURL=collection.data.js.map